import { useState, useEffect } from 'react'
import { api } from '../api'
import { riskPill, actionPill } from './Pill'
import { enumLabel, useI18n } from '../i18n'
import { scoreColor } from '../types'
import type { CrawlReportDetail } from '../types'

interface Props {
  reportId: string
  onBack: () => void
}

export function ReportDetail({ reportId, onBack }: Props) {
  const { t, fmt } = useI18n()
  const [report, setReport] = useState<CrawlReportDetail | null>(null)
  const [error, setError]   = useState<string | null>(null)

  useEffect(() => {
    api.getReportById(reportId)
      .then(setReport)
      .catch((err) => setError(err.message))
  }, [reportId])

  if (error) {
    return (
      <div className="detail-page">
        <button onClick={onBack} className="btn-back">← {t('common.back')}</button>
        <div className="table-empty">{t('reportDetail.error', { error })}</div>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="detail-page">
        <button onClick={onBack} className="btn-back">← {t('common.back')}</button>
        <div className="table-empty">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="detail-page">
      <button onClick={onBack} className="btn-back">{t('reportDetail.back')}</button>

      {/* ── Header ─────────────────────────────────── */}
      <div className="detail-header">
        <div>
          <h2>{report.merchantName}</h2>
          <div className="detail-url">
            <a href={report.url} target="_blank" rel="noopener">{report.url}</a>
            {report.finalUrl !== report.url && (
              <span className="td-muted"> → {report.finalUrl}</span>
            )}
          </div>
        </div>
        <div className="detail-score-badge" style={{ background: scoreColor(report.riskScore) }}>
          {report.riskScore}/100
        </div>
      </div>

      {/* ── Infos principales ──────────────────────── */}
      <div className="detail-grid">
        <div className="detail-field">
          <span className="detail-label">{t('reports.col.level')}</span>
          {riskPill(report.riskLevel)}
        </div>
        <div className="detail-field">
          <span className="detail-label">{t('common.actions')}</span>
          {actionPill(report.action)}
        </div>
        <div className="detail-field">
          <span className="detail-label">{t('common.country')}</span>
          <span>{report.countryFlag} {enumLabel(t, 'country', report.country, report.country)}</span>
        </div>
        <div className="detail-field">
          <span className="detail-label">{t('common.category')}</span>
          <span>{enumLabel(t, 'category', report.category, report.category)}</span>
        </div>
        <div className="detail-field">
          <span className="detail-label">HTTP</span>
          <span>{report.httpStatus}</span>
        </div>
        <div className="detail-field">
          <span className="detail-label">SSL</span>
          <span>{report.sslValid ? `✅ ${t('reportDetail.sslValid')}` : `❌ ${t('reportDetail.sslInvalid')}`}</span>
        </div>
        <div className="detail-field">
          <span className="detail-label">{t('reportDetail.responseTime')}</span>
          <span>{fmt.number(report.responseTimeMs)} ms</span>
        </div>
        <div className="detail-field">
          <span className="detail-label">{t('reportDetail.driftScore')}</span>
          <span>{report.driftScore != null ? fmt.decimal(report.driftScore, 2) : '—'}</span>
        </div>
        <div className="detail-field">
          <span className="detail-label">{t('reports.col.crawled')}</span>
          <span>{report.crawledAgo}</span>
        </div>
      </div>

      {/* ── Scores détaillés ───────────────────────── */}
      <div className="detail-section">
        <h3>{t('reportDetail.scoresTitle')}</h3>
        <div className="scores-row">
          {[
            { label: t('reportDetail.availability'), value: report.scores.availability },
            { label: t('reportDetail.regulatory'),   value: report.scores.regulatory },
            { label: t('reportDetail.vision'),       value: report.scores.vision },
          ].map((s) => (
            <div key={s.label} className="score-block">
              <div className="score-block-label">{s.label}</div>
              <div className="score-bar-large">
                <div
                  className="score-fill"
                  style={{ '--progress': s.value / 100, background: scoreColor(s.value) } as React.CSSProperties}
                />
              </div>
              <div className="score-block-value" style={{ color: scoreColor(s.value) }}>
                {s.value}/100
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Résumé IA ──────────────────────────────── */}
      {report.aiSummary && (
        <div className="detail-section">
          <h3>{t('reportDetail.aiTitle')}</h3>
          <div className="ai-summary">{report.aiSummary}</div>
        </div>
      )}

      {/* ── Raisons ────────────────────────────────── */}
      {report.reasons.length > 0 && (
        <div className="detail-section">
          <h3>{t('reportDetail.reasonsTitle')}</h3>
          <ul className="reasons-list">
            {report.reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {/* ── Mots-clés paiement ─────────────────────── */}
      {report.paymentKeywordsFound.length > 0 && (
        <div className="detail-section">
          <h3>{t('reportDetail.keywordsTitle')}</h3>
          <div className="keywords-row">
            {report.paymentKeywordsFound.map((kw) => (
              <span key={kw} className="keyword-tag">{kw}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── Screenshot ─────────────────────────────── */}
      {report.screenshotUrl && (
        <div className="detail-section">
          <h3>{t('reportDetail.screenshotTitle')}</h3>
          <a href={report.screenshotUrl} target="_blank" rel="noopener" className="screenshot-link">
            📸 {t('reportDetail.screenshotLink')}
          </a>
        </div>
      )}

      {/* ── Texte extrait ──────────────────────────── */}
      {report.visibleTextSnippet && (
        <div className="detail-section">
          <h3>{t('reportDetail.textTitle')}</h3>
          <pre className="text-snippet">{report.visibleTextSnippet}</pre>
        </div>
      )}
    </div>
  )
}
